package org.catharsis.spendtel.feature.support

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.FilterChip
import androidx.compose.material3.FilterChipDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import org.catharsis.spendtel.core.platform.openMailto
import org.catharsis.spendtel.ui.rememberKeyboardDismissAction
import org.catharsis.spendtel.feature.legal.LegalConstants
import org.catharsis.spendtel.feature.presentation.auth.authFieldColors
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelReportColors

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun ContactSupportForm(
    defaultName: String?,
    defaultEmail: String?,
    onSubmitted: (openedMailClient: Boolean) -> Unit,
    modifier: Modifier = Modifier,
) {
    var name by rememberSaveable { mutableStateOf(defaultName.orEmpty()) }
    var email by rememberSaveable { mutableStateOf(defaultEmail.orEmpty()) }
    var message by rememberSaveable { mutableStateOf("") }
    var topic by rememberSaveable { mutableStateOf(SupportTopic.Billing.name) }
    var errorMessage by rememberSaveable { mutableStateOf<String?>(null) }

    val selectedTopic = SupportTopic.entries.firstOrNull { it.name == topic } ?: SupportTopic.Billing
    val dismissKeyboard = rememberKeyboardDismissAction()

    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(16.dp),
    ) {
        Text(
            text = "Tell us what you need help with. Submitting opens your email app with a pre-filled message to our support team.",
            style = MaterialTheme.typography.bodyMedium,
            color = SpendTelReportColors.inkMuted,
            lineHeight = MaterialTheme.typography.bodyMedium.lineHeight,
        )

        OutlinedTextField(
            value = name,
            onValueChange = {
                name = it
                errorMessage = null
            },
            modifier = Modifier.fillMaxWidth(),
            label = { Text("Name") },
            singleLine = true,
            shape = RoundedCornerShape(16.dp),
            colors = authFieldColors(),
            keyboardOptions = KeyboardOptions(
                capitalization = KeyboardCapitalization.Words,
                imeAction = ImeAction.Next,
            ),
        )

        OutlinedTextField(
            value = email,
            onValueChange = {
                email = it
                errorMessage = null
            },
            modifier = Modifier.fillMaxWidth(),
            label = { Text("Email") },
            singleLine = true,
            shape = RoundedCornerShape(16.dp),
            colors = authFieldColors(),
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Email,
                imeAction = ImeAction.Next,
            ),
        )

        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Text(
                text = "Topic",
                style = MaterialTheme.typography.labelLarge,
                color = SpendTelReportColors.inkMuted,
            )
            FlowRow(
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                SupportTopic.entries.forEach { option ->
                    FilterChip(
                        selected = selectedTopic == option,
                        onClick = {
                            topic = option.name
                            errorMessage = null
                            dismissKeyboard()
                        },
                        label = { Text(option.label) },
                        colors = FilterChipDefaults.filterChipColors(
                            selectedContainerColor = SpendTelReportColors.violet.copy(alpha = 0.14f),
                            selectedLabelColor = SpendTelReportColors.violet,
                        ),
                    )
                }
            }
        }

        OutlinedTextField(
            value = message,
            onValueChange = {
                message = it
                errorMessage = null
            },
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = 4.dp),
            label = { Text("Message") },
            minLines = 5,
            shape = RoundedCornerShape(16.dp),
            colors = authFieldColors(),
            keyboardOptions = KeyboardOptions(
                capitalization = KeyboardCapitalization.Sentences,
                imeAction = ImeAction.Done,
            ),
            keyboardActions = KeyboardActions(
                onDone = { dismissKeyboard() },
            ),
        )

        errorMessage?.let { error ->
            Text(
                text = error,
                style = MaterialTheme.typography.bodySmall,
                color = SpendTelReportColors.rose,
            )
        }

        Button(
            onClick = {
                dismissKeyboard()
                val validationError = validateSupportRequest(name, email, message)
                if (validationError != null) {
                    errorMessage = validationError
                    return@Button
                }

                openMailto(
                    buildSupportMailto(
                        SupportRequest(
                            name = name,
                            email = email,
                            topic = selectedTopic,
                            message = message,
                        ),
                    ),
                ) { openedMailClient ->
                    onSubmitted(openedMailClient)
                }
            },
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(12.dp),
            colors = ButtonDefaults.buttonColors(
                containerColor = SpendTelReportColors.indigo,
                contentColor = Color.White,
            ),
        ) {
            Text(
                text = "Send to support",
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier.padding(vertical = 4.dp),
            )
        }

        Text(
            text = "You can also email us directly at ${LegalConstants.SUPPORT_EMAIL}.",
            style = MaterialTheme.typography.bodySmall,
            color = SpendTelReportColors.inkMuted,
        )
    }
}
